import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Send, Sparkles, X } from "lucide-react";
import { useLanguage } from "../i18n";
import { PAGE_CONTENT } from "../content-pages";

import WhatsAppButton from "./WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let id = localStorage.getItem("ogstay-chat-session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("ogstay-chat-session", id);
  }
  return id;
}

function renderText(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part
  );
}

export default function ConciergeChat() {
  const { lang } = useLanguage();
  const c = PAGE_CONTENT[lang].chat;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/chat/${sessionRef.current}`)
      .then((r) => r.json())
      .then((history) => {
        setMessages(history.length ? history : [{ role: "assistant", content: c.welcome }]);
      })
      .catch(() => setMessages([{ role: "assistant", content: c.welcome }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message, locale: lang }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) throw new Error("stream error");
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + parsed.delta };
                return copy;
              });
            }
          } catch (err) {
            if (err.message === "stream error") throw err;
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: c.error };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const showSuggestions = messages.length <= 1 && !streaming;

  return (
    <>
      <WhatsAppButton />
      <motion.button
        data-testid="concierge-open-button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? c.close : c.open}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2.2, type: "spring", stiffness: 260, damping: 20 }}
        className="fixed bottom-24 right-6 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-navy text-sky shadow-[0_16px_40px_-10px_rgba(15,43,58,0.6)] ring-2 ring-sky/40 transition-[transform,box-shadow] duration-300 hover:scale-110 hover:shadow-[0_20px_48px_-10px_rgba(75,179,232,0.5)] lg:bottom-6"
      >
        <AnimatePresence mode="wait">
          {open ? <X key="x" className="h-6 w-6" /> : <Sparkles key="spark" className="h-6 w-6" />}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="concierge-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-[70] flex h-[560px] max-h-[72vh] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-[1.75rem] bg-warm shadow-[0_40px_90px_-20px_rgba(15,43,58,0.55)] ring-1 ring-navy/10 sm:right-6"
          >
            <div className="flex items-center gap-3 bg-navy px-5 py-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-sky/15 text-sky">
                <Sparkles className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <p className="font-display text-lg font-semibold text-white">{c.title}</p>
                <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-sky">
                  <span className="h-1.5 w-1.5 rounded-full bg-sky" />
                  {c.sub}
                </p>
              </div>
            </div>

            <div ref={scrollRef} data-testid="concierge-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-5">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`concierge-message-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto rounded-br-md bg-navy text-white"
                      : "mr-auto rounded-bl-md bg-white text-ink ring-1 ring-navy/10"
                  }`}
                >
                  {renderText(m.content) || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              ))}
              {showSuggestions && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {c.suggestions.map((sug) => (
                    <button
                      key={sug}
                      data-testid={`concierge-suggestion-${sug.slice(0, 12).replace(/\s/g, "-")}`}
                      onClick={() => send(sug)}
                      className="rounded-full bg-white px-4 py-2 text-xs font-bold text-navy ring-1 ring-navy/15 transition-colors duration-300 hover:bg-navy hover:text-white"
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              data-testid="concierge-form"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-navy/10 bg-white p-3"
            >
              <input
                data-testid="concierge-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={c.placeholder}
                maxLength={1000}
                className="flex-1 rounded-full bg-warm px-5 py-3 text-sm font-semibold text-ink placeholder-mute/70 focus:outline-none focus:ring-2 focus:ring-sky/30"
              />
              <button
                data-testid="concierge-send-button"
                type="submit"
                disabled={streaming || !input.trim()}
                aria-label={c.send}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sky text-navy transition-[background-color,transform] duration-300 hover:scale-105 hover:bg-navy hover:text-white disabled:opacity-50"
              >
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
